import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Row, Col, Card, Statistic, Progress, List, Badge, Button, Alert, message } from 'antd';
import { Animated } from "react-animated-css";
import {
	UserOutlined,
	VideoCameraOutlined,
	EyeOutlined,
	CloudServerOutlined,
	WarningOutlined,
	DollarOutlined,
	TrophyOutlined,
	ClockCircleOutlined,
	ReloadOutlined
} from '@ant-design/icons';

import './Dashboard.scss';

class Dashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			refreshing: false,
			stats: {
				totalUsers: 0,
				onlineUsers: 0,
				activeStreams: 0,
				totalViews: 0,
				revenue: 0,
				serverLoad: 0
			},
			recentAlerts: [],
			topStreamers: [],
			systemHealth: {
				cpu: 0,
				memory: 0,
				bandwidth: 0,
				storage: 0
			}
		}
	}

	componentDidMount() {
		this.loadDashboardData();
		// Auto refresh every 30 seconds
		this.refreshInterval = setInterval(() => {
			this.refreshData();
		}, 30000);
	}

	componentWillUnmount() {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
		}
	}

	// Load all dashboard data
	loadDashboardData = () => {
		this.setState({ loading: true });
		
		Promise.all([
			this.fetchStats(),
			this.fetchAlerts(),
			this.fetchTopStreamers(),
			this.fetchSystemHealth()
		])
		.then(() => {
			this.setState({ loading: false });
		})
		.catch(error => {
			console.error('Error loading dashboard data:', error);
			message.error('Failed to load dashboard data');
			this.setState({ loading: false });
		});
	}

	// Refresh data without loading state
	refreshData = () => {
		this.setState({ refreshing: true });
		
		Promise.all([
			this.fetchStats(),
			this.fetchAlerts(),
			this.fetchTopStreamers(),
			this.fetchSystemHealth()
		])
		.then(() => {
			this.setState({ refreshing: false });
		})
		.catch(error => {
			console.error('Error refreshing dashboard data:', error);
			this.setState({ refreshing: false });
		});
	}

	// Fetch platform statistics
	fetchStats = () => {
		return new Promise((resolve) => {
			// TODO: Replace with actual API call
			// Example: return api.get('/admin/dashboard/stats')
			setTimeout(() => {
				const mockStats = {
					totalUsers: 15847 + Math.floor(Math.random() * 100),
					onlineUsers: 2341 + Math.floor(Math.random() * 50),
					activeStreams: 89 + Math.floor(Math.random() * 10),
					totalViews: 234567 + Math.floor(Math.random() * 1000),
					revenue: 45230 + Math.floor(Math.random() * 500),
					serverLoad: 60 + Math.floor(Math.random() * 20)
				};
				
				this.setState({ stats: mockStats });
				resolve(mockStats);
			}, 500);
		});
		
		/* TODO: Replace above with actual API call like this:
		return fetch('/api/admin/dashboard/stats', {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${this.props.userProfile.token}`,
				'Content-Type': 'application/json'
			}
		})
		.then(response => response.json())
		.then(data => {
			if (data.success) {
				this.setState({ stats: data.data });
				return data.data;
			} else {
				throw new Error(data.message);
			}
		});
		*/
	}

	// Fetch recent alerts
	fetchAlerts = () => {
		return new Promise((resolve) => {
			// TODO: Replace with actual API call
			setTimeout(() => {
				const mockAlerts = [
					{
						id: 1,
						type: 'warning',
						message: 'High server load detected on Stream Server 3',
						time: '2 minutes ago',
						severity: 'medium',
						timestamp: new Date().toISOString()
					},
					{
						id: 2,
						type: 'report',
						message: `${Math.floor(Math.random() * 10) + 1} new user reports pending review`,
						time: '15 minutes ago',
						severity: 'high',
						timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString()
					},
					{
						id: 3,
						type: 'system',
						message: 'CDN cache cleared successfully',
						time: '1 hour ago',
						severity: 'low',
						timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString()
					}
				];
				
				this.setState({ recentAlerts: mockAlerts });
				resolve(mockAlerts);
			}, 300);
		});
		
		/* TODO: Replace above with actual API call like this:
		return fetch('/api/admin/dashboard/alerts?limit=10', {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${this.props.userProfile.token}`,
				'Content-Type': 'application/json'
			}
		})
		.then(response => response.json())
		.then(data => {
			if (data.success) {
				this.setState({ recentAlerts: data.data });
				return data.data;
			} else {
				throw new Error(data.message);
			}
		});
		*/
	}

	// Fetch top streamers
	fetchTopStreamers = () => {
		return new Promise((resolve) => {
			// TODO: Replace with actual API call
			setTimeout(() => {
				const mockStreamers = [
					{ id: 1, name: 'StreamerA', viewers: 1234 + Math.floor(Math.random() * 100), status: 'live', avatar: null },
					{ id: 2, name: 'StreamerB', viewers: 987 + Math.floor(Math.random() * 100), status: 'live', avatar: null },
					{ id: 3, name: 'StreamerC', viewers: 756 + Math.floor(Math.random() * 100), status: 'live', avatar: null },
					{ id: 4, name: 'StreamerD', viewers: 543 + Math.floor(Math.random() * 100), status: 'live', avatar: null },
					{ id: 5, name: 'StreamerE', viewers: 432 + Math.floor(Math.random() * 100), status: 'live', avatar: null }
				];
				
				this.setState({ topStreamers: mockStreamers });
				resolve(mockStreamers);
			}, 400);
		});
		
		/* TODO: Replace above with actual API call like this:
		return fetch('/api/admin/dashboard/top-streamers?limit=5', {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${this.props.userProfile.token}`,
				'Content-Type': 'application/json'
			}
		})
		.then(response => response.json())
		.then(data => {
			if (data.success) {
				this.setState({ topStreamers: data.data });
				return data.data;
			} else {
				throw new Error(data.message);
			}
		});
		*/
	}

	// Fetch system health
	fetchSystemHealth = () => {
		return new Promise((resolve) => {
			// TODO: Replace with actual API call
			setTimeout(() => {
				const mockHealth = {
					cpu: 45 + Math.floor(Math.random() * 20),
					memory: 67 + Math.floor(Math.random() * 15),
					bandwidth: 78 + Math.floor(Math.random() * 10),
					storage: 34 + Math.floor(Math.random() * 5)
				};
				
				this.setState({ systemHealth: mockHealth });
				resolve(mockHealth);
			}, 200);
		});
		
		/* TODO: Replace above with actual API call like this:
		return fetch('/api/admin/dashboard/system-health', {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${this.props.userProfile.token}`,
				'Content-Type': 'application/json'
			}
		})
		.then(response => response.json())
		.then(data => {
			if (data.success) {
				this.setState({ systemHealth: data.data });
				return data.data;
			} else {
				throw new Error(data.message);
			}
		});
		*/
	}

	// Handle quick actions
	handleQuickAction = (action) => {
		console.log('Quick action:', action);
		
		switch (action) {
			case 'emergency':
				this.handleEmergencyBroadcast();
				break;
			case 'maintenance':
				this.handleMaintenanceMode();
				break;
			case 'streams':
				// Navigate to stream management
				this.props.history?.push('/stream-management');
				break;
			case 'users':
				// Navigate to user management
				this.props.history?.push('/user-management');
				break;
			case 'viewAllAlerts':
				// Navigate to alerts page or show modal
				console.log('View all alerts');
				break;
			default:
				break;
		}
	}

	// Handle emergency broadcast
	handleEmergencyBroadcast = () => {
		message.loading('Preparing emergency broadcast...', 1)
		.then(() => {
			// TODO: Replace with actual API call
			/* 
			return fetch('/api/admin/emergency-broadcast', {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${this.props.userProfile.token}`,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					message: 'System maintenance will begin in 10 minutes',
					priority: 'high'
				})
			})
			.then(response => response.json())
			.then(data => {
				if (data.success) {
					message.success('Emergency broadcast sent successfully');
				} else {
					throw new Error(data.message);
				}
			})
			.catch(error => {
				message.error('Failed to send emergency broadcast');
			});
			*/
			message.success('Emergency broadcast feature will be implemented');
		});
	}

	// Handle maintenance mode
	handleMaintenanceMode = () => {
		message.loading('Toggling maintenance mode...', 1)
		.then(() => {
			// TODO: Replace with actual API call
			/* 
			return fetch('/api/admin/maintenance-mode', {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${this.props.userProfile.token}`,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					enabled: true,
					message: 'System under maintenance'
				})
			})
			.then(response => response.json())
			.then(data => {
				if (data.success) {
					message.success('Maintenance mode updated');
				} else {
					throw new Error(data.message);
				}
			})
			.catch(error => {
				message.error('Failed to update maintenance mode');
			});
			*/
			message.success('Maintenance mode feature will be implemented');
		});
	}

	getSeverityColor = (severity) => {
		switch (severity) {
			case 'high': return '#ef4444';
			case 'medium': return '#f59e0b';
			case 'low': return '#10b981';
			default: return '#6b7280';
		}
	}

	render() {
		const { loading, refreshing, stats, recentAlerts, topStreamers, systemHealth } = this.state;

		return (
			<Animated animationIn="fadeIn">
				<div className="dashboard-wrapper" style={{ paddingBottom: (this.props.play) ? '100px' : '20px' }}>
					
					{/* Header with refresh button */}
					<div className="dashboard-header">
						<h2>Dashboard</h2>
						<Button 
							icon={<ReloadOutlined spin={refreshing} />}
							onClick={this.refreshData}
							loading={refreshing}
							size="small"
						>
							Refresh
						</Button>
					</div>
					
					{/* Stats Overview */}
					<Row gutter={[16, 16]} className="stats-row">
						<Col xs={24} sm={12} md={6}>
							<Card className="stat-card" loading={loading}>
								<Statistic
									title="Total Users"
									value={stats.totalUsers}
									prefix={<UserOutlined />}
									valueStyle={{ color: 'var(--text-primary)' }}
								/>
							</Card>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Card className="stat-card" loading={loading}>
								<Statistic
									title="Online Users"
									value={stats.onlineUsers}
									prefix={<EyeOutlined />}
									valueStyle={{ color: 'var(--text-highlight)' }}
								/>
							</Card>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Card className="stat-card" loading={loading}>
								<Statistic
									title="Active Streams"
									value={stats.activeStreams}
									prefix={<VideoCameraOutlined />}
									valueStyle={{ color: 'var(--accent)' }}
								/>
							</Card>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Card className="stat-card" loading={loading}>
								<Statistic
									title="Server Load"
									value={stats.serverLoad}
									suffix="%"
									prefix={<CloudServerOutlined />}
									valueStyle={{ color: stats.serverLoad > 80 ? 'var(--accent)' : 'var(--text-primary)' }}
								/>
							</Card>
						</Col>
					</Row>

					<Row gutter={[16, 16]} className="content-row">
						{/* System Health */}
						<Col xs={24} lg={12}>
							<Card title="System Health" className="system-health-card" loading={loading}>
								<div className="health-item">
									<span>CPU Usage</span>
									<Progress percent={systemHealth.cpu} size="small" />
								</div>
								<div className="health-item">
									<span>Memory Usage</span>
									<Progress percent={systemHealth.memory} size="small" />
								</div>
								<div className="health-item">
									<span>Bandwidth Usage</span>
									<Progress percent={systemHealth.bandwidth} size="small" />
								</div>
								<div className="health-item">
									<span>Storage Usage</span>
									<Progress percent={systemHealth.storage} size="small" />
								</div>
							</Card>
						</Col>

						{/* Recent Alerts */}
						<Col xs={24} lg={12}>
							<Card 
								title="Recent Alerts" 
								className="alerts-card"
								loading={loading}
								extra={<Button size="small" onClick={() => this.handleQuickAction('viewAllAlerts')}>View All</Button>}
							>
								<List
									size="small"
									dataSource={recentAlerts}
									renderItem={item => (
										<List.Item className="alert-item">
											<div className="alert-content">
												<Badge 
													color={this.getSeverityColor(item.severity)} 
													text={item.message}
												/>
												<div className="alert-time">
													<ClockCircleOutlined /> {item.time}
												</div>
											</div>
										</List.Item>
									)}
								/>
							</Card>
						</Col>
					</Row>

					<Row gutter={[16, 16]} className="content-row">
						{/* Top Streamers */}
						<Col xs={24} lg={12}>
							<Card title="Top Live Streamers" className="top-streamers-card" loading={loading}>
								<List
									size="small"
									dataSource={topStreamers}
									renderItem={(item, index) => (
										<List.Item className="streamer-item">
											<div className="streamer-info">
												<span className="rank">#{index + 1}</span>
												<span className="name">{item.name}</span>
												<Badge status="processing" text={`${item.viewers} viewers`} />
											</div>
										</List.Item>
									)}
								/>
							</Card>
						</Col>

						{/* Quick Actions */}
						<Col xs={24} lg={12}>
							<Card title="Quick Actions" className="quick-actions-card">
								<Row gutter={[8, 8]}>
									<Col span={12}>
										<Button 
											block 
											icon={<WarningOutlined />}
											onClick={() => this.handleQuickAction('emergency')}
										>
											Emergency Broadcast
										</Button>
									</Col>
									<Col span={12}>
										<Button 
											block 
											icon={<CloudServerOutlined />}
											onClick={() => this.handleQuickAction('maintenance')}
										>
											Maintenance Mode
										</Button>
									</Col>
									<Col span={12}>
										<Button 
											block 
											icon={<VideoCameraOutlined />}
											onClick={() => this.handleQuickAction('streams')}
										>
											Monitor Streams
										</Button>
									</Col>
									<Col span={12}>
										<Button 
											block 
											icon={<UserOutlined />}
											onClick={() => this.handleQuickAction('users')}
										>
											Manage Users
										</Button>
									</Col>
								</Row>
							</Card>
						</Col>
					</Row>

				</div>
			</Animated>
		);
	}
}

const mapStateToProps = state => ({
	router: state.router,
	userProfile: state.user.userProfile,
	selectedOrganization: state.user.selectedOrganization
});

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			// TODO: Add action creators here
		},
		dispatch
	);

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
